import { graphql, useStaticQuery } from "gatsby";
import React, { createRef, useEffect, useRef, useState } from "react";

import { useTheme } from "../../context/ThemeContext";
import { useFilteredPublicationsByLocationSlug } from "../../hooks/useFilteredPublicationsByLocationSlug";
import { uniqBy } from "../../utils/commonFunctions";
import { convertToSlug } from "../../utils/convertToSlug";
import { dateFormat } from "../../utils/dateFormat";
import {
  formatCurrencyAmount,
  renderCurrencyDateImage
} from "../../utils/formatContent";
import {
  bwMahlahlaProgramsTerm,
  defaultProgramTabsTitles
} from "../../utils/sharedDatasets";
import { DesktopTabletOnly, MobileOnly } from "../base/wrapper/device";
import Image from "../image";
import DatesAndPrices from "./datesAndPrices";
import ProgramAccordion from "./programAccordion";
import ProgramTabs from "./programTabs";
import Arrivals from "./tabs/arrivals";
import Careers from "./tabs/careers";
import Experiences from "./tabs/experiences";
import FreeTimeAndCulture from "./tabs/freetime";
import Itinerary from "./tabs/itinerary";
import LifeOnBase from "./tabs/lifeonbase";
import OurEthics from "./tabs/ourethics";
import Overview from "./tabs/overview";
import ParentInfo from "./tabs/parentInfo";
import Placements from "./tabs/placements";
import ProgramEthics from "./tabs/programEthics";
import Publications from "./tabs/publications";
import Qualifications from "./tabs/qualifications";
import SupportSafety from "./tabs/supportsafety";
import Team from "./tabs/team";
import Testimonials from "./tabs/testimonials";
import WhatsIncluded from "./tabs/whatsIncluded";
import WhatsItLike from "./tabs/whatsitlike";
import YourImpact from "./tabs/yourimpact";

const ProgramTabsAccordion = ({
  programDetails,
  programLocation,
  sharedDetails,
  testimonials,
  program,
  programHub,
  programPopular,
  programCapacity,
  experiences,
  expTabTitle,
  setAlumniOpenModal,
  location,
  setItineraryOpenModal,
  getActiveTabSlidebySlug,
  setActiveTabSlidebySlug
}) => {
  const { genericDetails, sortingProgramTypes } = useStaticQuery(graphql`
    query {
      genericDetails: wp {
        ...WpSharedProgramFragment
        globalProgramConfig: gviConfigSettings {
          acfGenericGlobalConfig {
            ...FeaturedLocationsFragment
          }
        }
      }
      sortingProgramTypes: allWpProgramType {
        nodes {
          title
          slug
          acfProgramsType {
            programTypeTabConfig {
              programTypePrgTabTitle
              programTypePrgTabIdentifier
            }
            benefitsTitleInternships
            benefitsInternships {
              title
              description
              icon {
                ...iconImagesData
              }
            }
          }
        }
      }
    }
  `);

  const { currentSiteMeta, currentBrand } = useTheme();

  const {
    acfProgramsMeta,
    taxonomyProgramSkills,
    title,
    databaseId,
    geographical
  } = program ?? {};

  const {
    whatItsLike,
    arrivalsInfo,
    ourEthics,
    lifeOnBaseOptions,
    flights,
    culturalImmersion,
    yourImpact,
    countryExploration,
    supportAndSafety,
    parentInfo,
    itinerary,
    whatsIncluded,
    training,
    internships,
    placements,
    programEthics,
    featuredBlocks
  } = genericDetails ?? {};

  const { globalProgramConfig } = sharedDetails ?? {};

  const { acfGenericGlobalConfig } = globalProgramConfig ?? {};

  const whatitslike = whatItsLike?.acfWhatItsLike || "";
  const arrivalsInfoDetails = arrivalsInfo?.arrivalInfo || "";

  const ourEthicsDetail = ourEthics?.ourEthics || "";
  const lifeOnBase = lifeOnBaseOptions?.lifeOnBase || "";
  const flightDetails = flights?.flights || "";
  const featuredBlocksDetails = featuredBlocks?.featuredBlocks || "";

  const teammembers =
    programHub?.teamMembers?.nodes?.flatMap((member) => member) ?? [];

  const culturalImmersionDetail = culturalImmersion?.culturalImmersion || "";
  const culturalPracticesList = programHub?.culturalPractices?.nodes ?? [];
  const yourImpactDetail = yourImpact?.yourImpact || "";
  const locationPages = programHub?.slug ?? "";
  const qualifications = programDetails.relatedQualifications
    ? programDetails.relatedQualifications
    : [];
  const programQualificationsContent =
    programDetails.programQualificationsContent ?? [];

  const countryExplorationDetail =
    countryExploration?.acfCountryExploration || "";
  const weekendTrips = uniqBy(programHub?.weekendTrips?.nodes, "id") ?? [];
  const localAdventures =
    uniqBy(programHub?.localAdventures?.nodes, "id") ?? [];
  const furtherTravels = uniqBy(programHub?.furtherTravels?.nodes, "id") ?? [];

  const supportSafety = supportAndSafety?.supportAndSafety || "";
  const parentInfoDetail = parentInfo?.parentInfo || "";
  const exclusiveDiscounts = acfGenericGlobalConfig?.exclusiveDiscount ?? [];
  const programTitle = title;

  const whatsIncludedDetail = whatsIncluded?.whatsIncluded ?? {};

  const coreInclusions = whatsIncludedDetail?.coreInclusions ?? [];

  const coreExclusions = whatsIncluded?.whatsIncluded.coreExclusions ?? [];

  const pageTitle = training?.pageTitle ?? [];
  const programTrainingTitle = training?.training?.programTrainingTitle ?? [];
  const trainingIntroductionText =
    training?.training?.trainingIntroductionText ?? [];
  const gviTrainingList = training?.training?.gviTraining ?? [];
  const hubTrainings = programHub?.hubTrainings?.nodes ?? [];
  const hubName = programHub?.name ?? [];
  const programEthicsDetail = programEthics?.programEthics?.programEthics ?? [];

  const programTypes = programDetails.programTypes?.flatMap(
    (item) => item.slug
  );

  const hasProductType = programTypes;

  const careers = internships?.gviInternships ?? [];
  const internshipType = programDetails?.internshipTypeProgram ?? "";

  const teamAcquaintedFamilyText =
    acfGenericGlobalConfig?.meetTheTeamGetAcquaintedText?.replace(
      "[LOCATION_PLACEHOLDER]",
      programLocation?.map((item) => item.text).join(", ") ?? ""
    );
  const teamIntro = acfGenericGlobalConfig?.meetTheTeamDesc ?? "";

  const programHighlights = programDetails?.pgmHighlights ?? [];
  const programActivities = programDetails?.pgmActivities ?? [];
  const programSkills = taxonomyProgramSkills?.nodes ?? [];
  const programPartners = programDetails?.partners ?? [];

  const overviewSectionDetails = {
    activitySectionTitle: acfGenericGlobalConfig?.pgmActivitySectionTitle ?? "",
    activitySectionDesc: acfGenericGlobalConfig?.pgmActivitySectionDesc ?? "",
    highlightSectionTitle:
      acfGenericGlobalConfig?.pgmHighlightSectionTitle ?? "",
    highlightSectionDesc: acfGenericGlobalConfig?.pgmHighlightSectionDesc ?? ""
  };

  const { placementsIntroText, placementListHeading, placementsTitle } =
    placements?.placements ?? {};

  const programPlacementPartners =
    acfProgramsMeta?.programPlacements?.map((item) => {
      return {
        name: item?.placementTitle || "",
        description: item?.placementText || "",
        image: item?.placementImage ?? ""
      };
    }) ?? [];

  const reformattedLocPages = [];
  const programHubLocations = programHub?.locationPages?.nodes ?? [];
  if (programHubLocations && programHubLocations?.length > 0) {
    programHubLocations.forEach((programHubLocation) => {
      reformattedLocPages.push(programHubLocation.slug);
    });
  }

  const publications = useFilteredPublicationsByLocationSlug({
    locationPageSlugs: reformattedLocPages
  });

  const itineraryDetails = itinerary?.itinerary || [];

  //filter the collection to the program location & product type
  const itineraryCollection = itineraryDetails?.itineraryCollection?.filter(
    (item) => {
      return (
        item?.gitcProductType?.some((type) =>
          programTypes?.includes(type.slug)
        ) &&
        item?.gitcGeographical?.some((geo) => locationPages?.includes(geo.slug))
      );
    }
  );

  const sharedItineraryList =
    itineraryCollection?.flatMap((itineraryCol) => {
      return itineraryCol?.gitcItineraryItems?.map((itineraryItem) => {
        return {
          itineraryDay: itineraryItem?.gitcItineraryItemDay,
          itineraryDescription: itineraryItem?.gitcItineraryItemDescription,
          itineraryImage: itineraryItem?.gitcItineraryItemImage,
          itineraryWeekday: itineraryItem?.gitcItineraryItemWeekday
        };
      });
    }) || [];

  const itineraryList =
    acfProgramsMeta?.programItinerary ?? sharedItineraryList ?? [];

  const programItineraryFile =
    acfProgramsMeta?.programItineraryFile?.mediaItemUrl ||
    (itineraryCollection?.length > 0
      ? itineraryCollection[0]?.gitcItineraryFile?.mediaItemUrl
      : null) ||
    null;

  const programGeographicals = geographical ?? [];
  const flatGeoSlugArr = programGeographicals?.nodes?.flatMap(
    (geo) => geo?.slug
  );

  const programFocuses = uniqBy(programDetails.programFocuses, "title") ?? [];

  const currencyToUse = currentSiteMeta?.currency
    ? currentSiteMeta.currency.toLowerCase()
    : process.env.GATSBY_PROJECT_CURRENCY ?? "gbp";

  const exclusiveOffers =
    acfGenericGlobalConfig?.programDatesAndPriceList ?? [];

  const exclusiveOffersDesc = exclusiveOffers[0]?.description;

  const exclusiveOfferAmounts =
    acfGenericGlobalConfig?.exclusiveOffersAmount ?? [];

  const exclusiveDiscountCode =
    exclusiveOfferAmounts?.find(
      (discount) => discount.exclOffersCurrencyIsoCode === currencyToUse
    ) ?? [];

  const exclusiveOfferAmount = exclusiveDiscountCode?.exclOffersAmount ?? "10";

  const exclusiveOfferAmountDisplay = formatCurrencyAmount({
    currentSiteMeta,
    amount: exclusiveOfferAmount
  });

  const scholarshipDisplayDescription = exclusiveOffersDesc?.replace(
    "SHORTCODE_ExclusiveOffersAmt",
    exclusiveOfferAmountDisplay
  );

  const offerDate = dateFormat(
    {
      month: "long",
      year: "numeric"
    },
    new Date()
  );

  const offerDescription =
    featuredBlocksDetails?.specialOfferDescription ??
    "Get up to 33% off this program if you book before the end of OFFER_DATE.";
  const specialOfferDescription = offerDescription?.replace(
    "OFFER_DATE",
    offerDate
  );
  const specialOffer = {
    offerHeading: featuredBlocksDetails?.specialOfferHeading ?? "",
    offerSubheading: featuredBlocksDetails?.specialOfferSubheading ?? "",
    offerDesc: specialOfferDescription ?? ""
  };

  const expTabSubtitle =
    countryExploration?.gviExperiencesTabSubtitle ??
    "Offered once a month, expand your adventure with GVI Experiences. These are just some of the activities offered on your program!";

  const isBushwiseMahlahla = new RegExp(bwMahlahlaProgramsTerm, "gi").test(
    programDetails?.programCode
  );

  const listTabConfig = (identifier) => {
    return (
      sortingProgramTypes?.nodes
        ?.map((programType, idx) => {
          return {
            slug: programType?.slug,
            title: programType?.title,
            configOpts: programType?.acfProgramsType?.programTypeTabConfig?.map(
              (config, idx) => {
                return {
                  id: config?.programTypePrgTabIdentifier,
                  genTabOrder: idx,
                  genTabTitle: config?.programTypePrgTabTitle
                };
              }
            )
          };
        })
        ?.filter((item) => item?.configOpts?.length > 0)
        ?.filter((item) => programTypes?.includes(item?.slug))
        ?.shift()
        ?.configOpts.filter((item) => item?.id === identifier)
        ?.shift() ||
      defaultProgramTabsTitles?.find((item) => item?.id === identifier) || {
        genTabTitle: identifier,
        genTabOrder: 50
      }
    );
  };

  //list of benefits based on product type
  const productTypeBenefits =
    sortingProgramTypes?.nodes
      ?.map((programType, idx) => {
        return {
          slug: programType?.slug || "",
          title: programType?.acfProgramsType?.benefitsTitleInternships,
          items: programType?.acfProgramsType?.benefitsInternships?.map(
            (item) => {
              return {
                title: item?.title,
                description: item?.description,
                icon: item?.icon
              };
            }
          )
        };
      })
      ?.filter((item) => programTypes?.includes(item?.slug))
      ?.shift() || [];

  const tabsDetails = [
    {
      ...listTabConfig("overview"),
      tabComponent: (
        <Overview
          projectOverview={programDetails.projectOverview}
          programFocus={programFocuses}
          programTypes={programDetails.programTypes}
          programLocation={programLocation}
          programHighlights={programHighlights}
          programActivities={programActivities}
          programPartners={programPartners}
          programSkills={programSkills}
          programSectionTitles={overviewSectionDetails}
          programGeoSlugs={flatGeoSlugArr}
          key={"overview-block"}
        />
      )
    },

    {
      ...listTabConfig("dates_prices"),
      tabIcon: (
        <Image
          imageAlt={""}
          image={""}
          imageClass="inline pr-2"
          sourceUrl={renderCurrencyDateImage({ currentSiteMeta, currentBrand })}
        />
      ),
      tabComponent: (
        <DatesAndPrices
          programId={databaseId}
          programCode={programDetails.programCode}
          researchProgramCode={programDetails.researchInternshipProgramCode}
          careerProgramCode={programDetails.careerInternshipProgramCode}
          programTypes={programDetails.programTypes}
          exclusiveDiscounts={exclusiveDiscounts}
          programPopular={programPopular}
          programCapacity={programCapacity}
          exclusiveOffers={exclusiveOffers}
          scholarshipDisplayDescription={scholarshipDisplayDescription}
          specialOffer={specialOffer}
        />
      )
    },

    itineraryDetails &&
      itineraryList.length > 0 && {
        ...listTabConfig("itinerary"),
        tabComponent: (
          <Itinerary
            itineraryDetails={itineraryDetails}
            itineraryList={itineraryList}
            programTypes={programTypes}
            setItineraryOpenModal={setItineraryOpenModal}
            programItineraryFile={programItineraryFile}
          />
        )
      },

    internshipType === "Apprenticeship" &&
      programPlacementPartners?.length > 0 && {
        ...listTabConfig("placements"),
        tabComponent: (
          <Placements
            placementsHeading={placementsTitle}
            placementsSubHeading={"Partners"}
            placementsIntroText={placementsIntroText}
            placementsSubText={placementListHeading}
            partners={programPlacementPartners}
          />
        )
      },
    whatsIncludedDetail && {
      ...listTabConfig("whats_included"),
      tabComponent: (
        <WhatsIncluded
          whatsIncluded={coreInclusions}
          whatsExcluded={coreExclusions}
          hasProductType={hasProductType}
        />
      )
    },
    !programTypes?.includes("under-18") && {
      ...listTabConfig("life_on_base"),
      tabComponent: (
        <LifeOnBase lifeonbase={lifeOnBase} hubdetails={programHub} />
      )
    },

    !programTypes?.includes("under-18") &&
      !isBushwiseMahlahla &&
      experiences && {
        ...listTabConfig("experiences"),
        tabState: "New",
        tabComponent: (
          <Experiences
            gviExperiencesLabel={expTabTitle}
            gviExperiencesDescription={
              countryExplorationDetail.gviExperiencesDescription
            }
            expTabSubtitle={expTabSubtitle}
            experiences={experiences}
          />
        )
      },

    (countryExplorationDetail || culturalImmersionDetail) &&
      !programTypes?.includes("under-18") && {
        ...listTabConfig("free_time"),
        tabComponent: (
          <FreeTimeAndCulture
            countryExploration={countryExplorationDetail}
            weekendTrips={weekendTrips}
            localAdventures={localAdventures}
            furtherTravels={furtherTravels}
            culturalImmersionDetail={culturalImmersionDetail}
            culturalPracticesList={culturalPracticesList}
          />
        )
      },

    testimonials &&
      testimonials.length > 0 && {
        ...listTabConfig("reviews"),
        tabComponent: <Testimonials testimonials={testimonials} />
      },

    whatitslike && {
      ...listTabConfig("speak_to_alumni"),
      tabComponent: (
        <WhatsItLike
          whatItsLike={whatitslike}
          setAlumniOpenModal={setAlumniOpenModal}
        />
      )
    },
    teammembers &&
      teammembers.length > 0 && {
        ...listTabConfig("meet_the_team"),
        tabComponent: (
          <Team
            teamMembers={teammembers}
            programTitle={programTitle}
            teamAcquaintedFamilyText={teamAcquaintedFamilyText}
            teamIntro={teamIntro}
          />
        )
      },
    !programTypes?.includes("under-18") &&
      parentInfoDetail && {
        ...listTabConfig("parent_info"),
        genTabTitle: `${parentInfoDetail.introLabel}`,
        tabComponent: <ParentInfo parentInfo={parentInfoDetail} />
      },

    arrivalsInfoDetails && {
      ...listTabConfig("arrivals"),
      tabComponent: (
        <Arrivals
          arrivalsInfo={arrivalsInfoDetails}
          locations={locationPages}
          flights={flightDetails}
        />
      )
    },

    yourImpactDetail &&
      !isBushwiseMahlahla && {
        ...listTabConfig("your_impact"),
        tabComponent: (
          <YourImpact yourImpact={yourImpactDetail} locations={locationPages} />
        )
      },

    publications?.list.length > 0 && {
      ...listTabConfig("publications"),
      tabComponent: <Publications publicationList={publications} />
    },

    ourEthicsDetail && {
      ...listTabConfig("our_ethics"),
      tabComponent: <OurEthics ourEthics={ourEthicsDetail} />
    },

    {
      ...listTabConfig("program_ethics"),
      tabComponent: (
        <ProgramEthics
          ethics={programEthicsDetail}
          focuses={programDetails.programFocuses}
        />
      )
    },

    qualifications &&
      qualifications.length > 0 &&
      pageTitle &&
      pageTitle.length > 0 && {
        ...listTabConfig("qualification_training"),
        tabComponent: (
          <Qualifications
            qualifications={qualifications}
            programTrainingTitle={programTrainingTitle}
            trainingIntroductionText={trainingIntroductionText}
            gviTrainingList={gviTrainingList}
            hubTrainings={hubTrainings}
            hubName={hubName}
            programQualificationsContent={programQualificationsContent}
          />
        )
      },

    careers &&
      (programTypes?.includes("internship") ||
        programTypes?.includes("research-fellowship") ||
        programTypes?.includes("professional-apprenticeship")) &&
      !isBushwiseMahlahla && {
        ...listTabConfig("careers"),
        tabComponent: (
          <Careers
            careers={careers}
            internshipType={internshipType}
            benefits={productTypeBenefits}
          />
        )
      },
    programTypes?.includes("under-18") &&
      parentInfoDetail && {
        ...listTabConfig("parent_info"),
        genTabTitle: `${parentInfoDetail.introLabel}`,
        tabComponent: <ParentInfo parentInfo={parentInfoDetail} />
      },

    supportSafety && {
      ...listTabConfig("support"),
      tabComponent: <SupportSafety supportDetails={supportSafety} />
    }
  ]
    .filter((trimEmpty) => trimEmpty)
    ?.sort((a, b) => a.genTabOrder - b.genTabOrder);

  const [currentSlide, setCurrentSlide] = useState(0);
  const tabsRef = useRef(null);
  const [isTab, setIsTab] = useState(false);
  const goToScroll = () => tabsRef?.current?.scrollIntoView();

  const tabs = tabsDetails?.map((tab, index) => {
    return {
      title: tab?.genTabTitle,
      id: index,
      // text: tab?.tabContentDesc,
      accordionSlug: convertToSlug(tab?.genTabTitle),
      comp: tab?.tabComponent,
      tabState: tab?.tabState,
      tabIcon: tab?.tabIcon || null,
      ref: createRef()
    };
  });

  useEffect(() => {
    let url = new URL(location.href);
    const currentTabTitle = url?.searchParams?.get("tab");

    if (!currentTabTitle) {
      setCurrentSlide(0);
      setIsTab(false);
    } else {
      setIsTab(true);
      const tab = tabs?.filter(
        (tab) => convertToSlug(tab.title) === currentTabTitle
      );

      setCurrentSlide(tab ? tab[0]?.id : currentSlide);
      goToScroll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (getActiveTabSlidebySlug) {
      const tab = tabs?.filter(
        (tab) => convertToSlug(tab.title) === getActiveTabSlidebySlug
      );

      setCurrentSlide(tab ? tab[0]?.id : currentSlide);
    }
  }, [getActiveTabSlidebySlug]);


  return (
    <>
      <DesktopTabletOnly>
        <ProgramTabs
          tabs={tabs}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          setIsTab={setIsTab}
          isTab={isTab}
        />
      </DesktopTabletOnly>
      <MobileOnly>
        <ProgramAccordion
          accordions={tabs}
          location={location}
          getActiveTabSlidebySlug={getActiveTabSlidebySlug}
          setActiveTabSlidebySlug={setActiveTabSlidebySlug}
        />
      </MobileOnly>
    </>
  );
};

export default ProgramTabsAccordion;
